import React from "react";
import { useTranslation } from "react-i18next";
import InputInline from "../../../components/InputInline";

interface WebsiteInputFieldProps {
  value?: string | number;
  onChange?: (value: string, error: boolean) => void;
  onFinish?: (value: string, error: boolean) => void;
  placeholder?: string;
  error?: boolean;
  required?: boolean;
  border?: boolean;
  showError?: boolean;
  isGrid?: boolean;
  variant?: "default" | "inline";
}

const WebsiteInputField: React.FC<WebsiteInputFieldProps> = ({
  value = "",
  onChange = () => {},
  onFinish = () => {},
  placeholder = "",
  error = false,
  required = false,
  border = true,
  showError = false,
  isGrid = false,
  variant = "default",
}) => {
  const { t } = useTranslation();

  const formatWebsite = (url: string) => {
    if (!url.trim()) return url;

    const cleanUrl = url.replace(/^https?:\/\//, "");

    return `https://${cleanUrl}`;
  };

  const validateWebsite = (url: string) => {
    if (required && !url.trim()) {
      return true;
    }

    if (!url.trim()) {
      return false;
    }

    try {
      const urlPattern =
        /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/;
      return !urlPattern.test(url);
    } catch {
      return true;
    }
  };

  const handleInputChange = (newValue: string) => {
    const hasError = validateWebsite(newValue);
    onChange(newValue, hasError);
  };

  const handleFinish = (finalValue: string, save?: boolean) => {
    if (!save) return;

    let processedValue = finalValue;

    if (processedValue.trim() && !processedValue.startsWith("http")) {
      processedValue = formatWebsite(processedValue);
    }

    const hasError = validateWebsite(processedValue);
    onFinish(processedValue, hasError);
  };

  const renderError = () => {
    if (error && required) {
      return null;
    }
    if (error) {
      return (
        <p className="mt-2 text-sm text-red-600">
          {required ? t("This field is required") : t("Invalid website format")}
        </p>
      );
    }
  };

  return (
    <>
      <InputInline
        type="url"
        error={error}
        placeholder={isGrid ? "" : placeholder || "https://example.com"}
        value={value}
        variant={variant}
        border={isGrid ? false : border}
        onChange={handleInputChange}
        onFinish={handleFinish}
      />
      {error && showError && renderError()}
    </>
  );
};

export default WebsiteInputField;
